import { FC, ReactNode, useEffect, useState } from 'react';
import { Box, Theme, Typography, useTheme } from '@mui/material';
import type { LdtWorkflowDefinition } from '@ldt/workflows/types';
import { FieldDisplay } from 'src/components/field_display';
import { Warning } from 'src/components/icons/warning';
import {
  useWorkflow,
  useWorkflowType,
} from 'src/distribution_platforms/features/hooks/use_workflow';
import { useIsLdtSalesOrCustomerSuccessUser } from 'src/hooks/use_auth';
import {
  ArteraFaxStatus,
  LdtOrderDetailFieldsFragment,
  OrderBaseFieldsFragment,
  OrderStatus,
  SpecimenType,
} from 'src/types';
import { DistributionPlatform } from 'src/types/env';
import { WorkflowType } from 'src/types/workflows';

// TODO(joshm): Refactor into separate components for each platform.
/** Shared order detail fields plus optional LDT-only status fields. */
type OrderStatusSectionOrder = OrderBaseFieldsFragment &
  Partial<
    Pick<
      LdtOrderDetailFieldsFragment,
      'mostRecentPathologyTrrFax' | 'imageQcRejectionReason'
    >
  >;

export const OrderStatusSection: FC<{
  order: OrderStatusSectionOrder;
  platform: DistributionPlatform;
}> = ({ order, platform }) => {
  const theme = useTheme();
  const workflow = useWorkflow();
  const workflowType = useWorkflowType();
  const isCSOrSalesUser = useIsLdtSalesOrCustomerSuccessUser();
  const { mostRecentPathologyTrrFax } = order;

  const mostRecentPathologyTrrFaxStatusMessage =
    getMostRecentPathologyTrrFaxStatusMessage(
      mostRecentPathologyTrrFax?.status,
    );
  const showSamdAiErrorMessage =
    platform === DistributionPlatform.Samd &&
    order.status === OrderStatus.AiError &&
    order.statusMessage;
  const ldtOrder =
    platform === DistributionPlatform.Ldt
      ? (order as LdtOrderDetailFieldsFragment)
      : null;
  const ldtMissingInformationMessages = ldtOrder
    ? (workflow as LdtWorkflowDefinition).getOrderMissingInfoMessages(ldtOrder)
    : [];
  const showLdtMissingInformationMessages =
    platform === DistributionPlatform.Ldt &&
    order.status &&
    [
      OrderStatus.QualityControlPending,
      OrderStatus.SlideImageQualityControlPending,
    ].includes(order.status) &&
    ldtMissingInformationMessages.length > 0;
  const showMostRecentPathologyTrrFaxStatusMessage =
    platform === DistributionPlatform.Ldt &&
    order.status &&
    [
      OrderStatus.ReportFaxed,
      OrderStatus.ReportFaxPending,
      OrderStatus.ReportFaxFailed,
      OrderStatus.ReportReady,
    ].includes(order.status) &&
    mostRecentPathologyTrrFaxStatusMessage;
  const showImageQcRejectionReason =
    platform === DistributionPlatform.Ldt &&
    workflowType === WorkflowType.DIRECT_UPLOAD_LAB &&
    order.imageQcRejectionReason &&
    order.imageQcRejectionReason.trim().length > 0 &&
    order.status === OrderStatus.SlideImageQualityControlFailed;

  // Track validation errors state (async call). Only LDT workflow has this.
  const [hasValidationErrors, setHasValidationErrors] =
    useState<boolean>(false);

  useEffect(() => {
    if (platform === DistributionPlatform.Ldt && ldtOrder) {
      const checkValidationErrors = async () => {
        const hasErrors = await (
          workflow as LdtWorkflowDefinition
        ).checkOrderValidationErrors({
          order: ldtOrder,
        });
        setHasValidationErrors(hasErrors);
      };

      checkValidationErrors();
    }
  }, [ldtOrder, order, platform, workflow, isCSOrSalesUser]);
  const showLdtFormValidationMessage =
    platform === DistributionPlatform.Ldt &&
    isCSOrSalesUser &&
    order.status === OrderStatus.QualityControlPending &&
    [SpecimenType.ProstateBiopsy, SpecimenType.ProstatePostRp].includes(
      order.specimenType,
    ) &&
    !showLdtMissingInformationMessages &&
    hasValidationErrors;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <FieldDisplay
        variant='horizontal'
        field={{
          label: 'Order Date',
          value: order.submittedAt
            ? new Date(order.submittedAt).toLocaleDateString()
            : 'Not submitted',
        }}
      />
      <FieldDisplay
        variant='horizontal'
        field={{
          label: 'Status Updated',
          value: order.statusUpdatedAt
            ? new Date(order.statusUpdatedAt).toLocaleDateString()
            : '',
        }}
      />
      {showImageQcRejectionReason && (
        <WarningMessageField
          label='Image QC Rejection Reason'
          theme={theme}
          message={order.imageQcRejectionReason}
        />
      )}
      {showMostRecentPathologyTrrFaxStatusMessage && (
        <Box
          sx={{
            p: 1.5,

            bgcolor: getMostRecentPathologyTrrFaxStatusBgColor(
              theme,
              mostRecentPathologyTrrFax?.status,
            ),

            borderRadius: 1,
            maxWidth: 400,
          }}
        >
          <Typography
            color={theme.palette.common.black}
            sx={{
              padding: 0,
              fontSize: theme.typography.caption.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.25,
            }}
          >
            {mostRecentPathologyTrrFaxStatusMessage}
          </Typography>
        </Box>
      )}
      {showSamdAiErrorMessage && (
        <Box
          sx={{
            p: 1.5,
            bgcolor: theme.palette.error.light,
            borderRadius: 1,
            maxWidth: 400,
          }}
        >
          <Typography
            color={theme.palette.common.black}
            sx={{
              padding: 0,
              fontSize: theme.typography.caption.fontSize,
              fontWeight: theme.typography.fontWeightMedium,
              lineHeight: 1.25,
            }}
          >
            {order.statusMessage}
          </Typography>
        </Box>
      )}
      {showLdtMissingInformationMessages && (
        <WarningMessageField
          label='Missing Information'
          theme={theme}
          message={ldtMissingInformationMessages.join('\n\n')}
        />
      )}
      {showLdtFormValidationMessage && (
        <WarningMessageField
          label='Corrections Required'
          theme={theme}
          message={
            <>
              The order cannot be QC Approved until the order information is
              corrected. Please edit the order and revise the information.
            </>
          }
        />
      )}
    </Box>
  );
};

const WarningMessageField: FC<{
  label: string;
  theme: Theme;
  message: ReactNode;
}> = ({ label, theme, message }) => (
  <Box>
    <FieldDisplay
      variant='horizontal'
      field={{
        label,
        icon: (
          <Warning
            fill={theme.palette.text.secondary}
            sx={{
              fontSize: (theme) => theme.typography.body1.fontSize,
            }}
          />
        ),
      }}
    />
    <Box
      sx={{
        mt: 1,
        p: 1.5,
        bgcolor: theme.palette.error.light,
        borderRadius: 1,
        maxWidth: 400,
      }}
    >
      <Typography
        color={theme.palette.common.black}
        sx={{
          padding: 0,
          fontSize: theme.typography.caption.fontSize,
          fontWeight: theme.typography.fontWeightMedium,
          lineHeight: 1.25,
          whiteSpace: 'pre-line',
        }}
      >
        {message}
      </Typography>
    </Box>
  </Box>
);

/**
 * @param faxStatus - The status of the most recent pathology TRR fax.
 * @returns The message for the most recent pathology TRR fax status.
 */
const getMostRecentPathologyTrrFaxStatusMessage = (
  faxStatus?: ArteraFaxStatus | null,
) => {
  switch (faxStatus) {
    case ArteraFaxStatus.Success:
      return 'Test report faxed to the pathology lab.';
    case ArteraFaxStatus.Pending:
      return 'Faxing test report to the pathology lab.';
    case ArteraFaxStatus.Failure:
      return 'Failed to fax the test report to the pathology lab.';
    case ArteraFaxStatus.Canceled:
      return 'Test report fax to pathology lab was canceled.';
  }
};

/**
 * @param faxStatus - The status of the most recent pathology TRR fax.
 * @returns The color for the background of the most recent pathology TRR fax status message.
 */
const getMostRecentPathologyTrrFaxStatusBgColor = (
  theme: Theme,
  faxStatus?: ArteraFaxStatus | null,
) => {
  switch (faxStatus) {
    case ArteraFaxStatus.Success:
      return theme.palette.success.light;
    case ArteraFaxStatus.Pending:
      return theme.palette.warning.light;
    case ArteraFaxStatus.Failure:
      return theme.palette.error.light;
    case ArteraFaxStatus.Canceled:
      return theme.palette.error.light;
  }
};
